// src/components/categories/CategoryForm.tsx
import { useState } from 'react';
import { toast } from 'sonner';
import { categoriesApi, type Category } from '../../api/categories';

interface CategoryFormProps {
    initialData?: {
        id?: string;
        name: string;
        isActive: boolean;
    };
    onSuccess?: (newCategory: Category) => void;
    onCancel: () => void;
}

export function CategoryForm({
    initialData,
    onSuccess,
    onCancel,
}: CategoryFormProps) {
    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        isActive: initialData?.isActive ?? true,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{title?: string}>({title: ""})

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setErrors({title: "Category name is required"})
            return;
        }
        else if(formData.name.trim().length < 2){
            setErrors({title: "Name must be at least 2 characters"})
            return;
        }

        setIsLoading(true);

        try {
            if (initialData?.id) {
                // Update existing category
                const updatedCategory = await categoriesApi.updateCategory(initialData.id, formData);

                if(updatedCategory?.success){
                    toast.success('Category updated successfully');
                    onSuccess?.(updatedCategory.data);
                }
                else{
                    toast.error(updatedCategory.message)
                }
            } else {
                // Create new category
                const newCategory = await categoriesApi.createCategory(formData);

                if(newCategory?.success){
                    toast.success('Category created successfully');
                    onSuccess?.(newCategory.data);
                }
                else{
                    toast.error(newCategory.message)
                }
            }
        } catch (error) {
            console.error('Error saving category:', error);
            toast.error('Failed to save category');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder='Category name'
                    className="w-full pl-10 pr-10 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
                    required
                    disabled={isLoading}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>
            <div className="flex items-center">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <label
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${formData.isActive
                                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                                : 'bg-black/20 border-white/10 text-slate-400 hover:bg-white/5 hover:text-white'
                            }`}
                    >
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="w-4 h-4 rounded border-white/20 bg-black/20 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                            disabled={isLoading}
                        />
                        <span className="text-sm font-medium">Active</span>
                    </label>
                </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={isLoading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={isLoading}
                >
                    {isLoading
                        ? 'Saving...'
                        : initialData?.id
                            ? 'Update'
                            : 'Create'} Category
                </button>
            </div>
        </form>
    );
}